import React, { useState, useEffect } from 'react';
import { useSocket } from '../../../hooks/useSocket';
import { chatAPI } from '../../../services/api';
import notificationService from '../../../services/notificationService';
import StudentConversationList from './StudentConversationList';
import StudentMessageArea from './StudentMessageArea';
import StudentNewChatModal from './StudentNewChatModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const StudentChatInterface = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState({});
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({}); // Track online status of users
  const [lastSeen, setLastSeen] = useState({}); // Track last seen timestamps
  const [showConversationList, setShowConversationList] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState(notificationService.getPermission());

  // Get student info from localStorage
  const token = localStorage.getItem('studentAuthToken');
  const studentStr = localStorage.getItem('student');
  const student = studentStr ? JSON.parse(studentStr) : null;

  // Extract data from token
  let organizationId = student?.organization_id;
  let studentId = student?._id || student?.id;

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Extract organization_id from token if not in student object
      if (!organizationId) {
        organizationId = payload.org_id;
        console.log('📋 Extracted org_id from token:', organizationId);
      }

      // Extract student_id from token (sub field contains student_id)
      if (!studentId) {
        // The token 'sub' contains the student_id, we need to fetch the actual MongoDB _id
        // For now, we'll need to fetch student info
        studentId = payload.sub; // This is student_id, not MongoDB _id
        console.log('📋 Extracted student_id from token:', studentId);
      }
    } catch (e) {
      console.error('Failed to decode token:', e);
    }
  }

  console.log('🎓 Student Chat - Connection params:', {
    hasToken: !!token,
    studentId,
    userType: 'student',
    organizationId,
    studentObject: student
  });

  // Initialize WebSocket
  const {
    sendMessage,
    sendTyping,
    markAsRead,
    onNewMessage,
    onUserTyping,
    onMessagesRead,
    onUserPresence,
    off,
    isConnected
  } = useSocket(
    token,
    studentId,
    'student',
    organizationId
  );

  // Fetch student info to get MongoDB _id
  const [studentMongoId, setStudentMongoId] = useState(null);

  useEffect(() => {
    const fetchStudentInfo = async () => {
      try {
        // Fetch student info from API to get the MongoDB _id
        const response = await fetch(`${API_URL}/api/student-auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setStudentMongoId(data._id || data.id);
        console.log('📝 Student MongoDB ID:', data._id || data.id);
      } catch (error) {
        console.error('Failed to fetch student info:', error);
      }
    };

    if (token && !studentMongoId) {
      fetchStudentInfo();
    }
  }, [token, studentMongoId]);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Set up WebSocket listeners
  useEffect(() => {
    const handleNewMessage = (message) => {
      // Add message to the appropriate conversation
      if (message.conversation_id === selectedConversation?.id) {
        setMessages(prev => [...prev, message]);
        markAsRead(selectedConversation.id);
      }

      // Show browser notification if message is from another user and conditions are met
      if (message.sender_id !== studentMongoId) {
        const shouldNotify = notificationService.shouldShowNotification(
          message.conversation_id,
          selectedConversation?.id
        );

        if (shouldNotify) {
          const senderName = message.sender_name || 'Someone';
          notificationService.showMessageNotification(
            senderName,
            message.content,
            message.conversation_id,
            (conversationId) => {
              // Handle notification click - select the conversation
              const conversation = conversations.find(conv => conv.id === conversationId);
              if (conversation) {
                handleSelectConversation(conversation);
              }
            }
          );
        }
      }

      // Update conversation list - update the specific conversation instead of refetching all
      setConversations(prev => {
        return prev.map(conv => {
          if (conv.id === message.conversation_id) {
            return {
              ...conv,
              last_message: message.content,
              last_message_time: message.created_at,
              unread_count: message.sender_id === studentMongoId ? 0 : (message.conversation_id === selectedConversation?.id ? 0 : (conv.unread_count || 0) + 1)
            };
          }
          return conv;
        }).sort((a, b) => {
          const timeA = a.last_message_time ? new Date(a.last_message_time) : new Date(0);
          const timeB = b.last_message_time ? new Date(b.last_message_time) : new Date(0);
          return timeB - timeA;
        });
      });
    };

    const handleUserTyping = (data) => {
      setTypingUsers(prev => ({
        ...prev,
        [data.conversation_id]: {
          userId: data.user_id,
          isTyping: data.is_typing
        }
      }));

      // Clear typing indicator after 3 seconds
      if (data.is_typing) {
        setTimeout(() => {
          setTypingUsers(prev => {
            const updated = { ...prev };
            delete updated[data.conversation_id];
            return updated;
          });
        }, 3000);
      }
    };

    const handleMessagesRead = (data) => {
      // Update read status in messages
      if (data.conversation_id === selectedConversation?.id) {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          is_read: true
        })));
      }
    };

    const handleUserPresence = (data) => {
      // Update online status for a user
      console.log('👤 Presence update:', data);
      setOnlineUsers(prev => ({
        ...prev,
        [data.user_id]: data.is_online
      }));

      // Update last seen if user went offline
      if (!data.is_online && data.last_seen) {
        setLastSeen(prev => ({
          ...prev,
          [data.user_id]: data.last_seen
        }));
      }
    };

    onNewMessage(handleNewMessage);
    onUserTyping(handleUserTyping);
    onMessagesRead(handleMessagesRead);
    onUserPresence(handleUserPresence);

    return () => {
      off('new_message');
      off('user_typing');
      off('messages_read');
      off('user_presence');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation, studentMongoId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getStudentConversations();
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await chatAPI.getConversationMessages(conversationId);
      setMessages(response.data.messages);

      // Mark as read
      await chatAPI.markConversationRead(conversationId);
      markAsRead(conversationId);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setShowConversationList(false); // Hide conversation list on mobile when a conversation is selected
    fetchMessages(conversation.id);

    // Fetch initial presence status for the other participant
    const otherParticipantId = conversation.participants.find(id => id !== studentMongoId);
    if (otherParticipantId) {
      try {
        const response = await chatAPI.getPresenceStatus([otherParticipantId]);
        const presenceData = response.data;
        if (presenceData[otherParticipantId]) {
          setOnlineUsers(prev => ({
            ...prev,
            [otherParticipantId]: presenceData[otherParticipantId].is_online
          }));
          if (presenceData[otherParticipantId].last_seen) {
            setLastSeen(prev => ({
              ...prev,
              [otherParticipantId]: presenceData[otherParticipantId].last_seen
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching presence status:', error);
      }
    }
  };

  const handleSendMessage = (content) => {
    if (!selectedConversation) return;

    // Find the recipient (the other participant)
    const recipientId = selectedConversation.participants.find(
      id => id !== studentMongoId
    );

    sendMessage(selectedConversation.id, content, recipientId);
  };

  const handleTyping = (isTyping) => {
    if (!selectedConversation) return;
    sendTyping(selectedConversation.id, isTyping);
  };

  const handleConversationCreated = (conversation) => {
    // Add new conversation to the list if not already present
    setConversations(prev => {
      const exists = prev.find(conv => conv.id === conversation.id);
      if (exists) {
        return prev;
      }
      return [conversation, ...prev];
    });

    // Select the new conversation
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleRequestNotificationPermission = async () => {
    const permission = await notificationService.requestPermission();
    setNotificationPermission(permission);
  };

  const handleDismissNotificationBanner = () => {
    // Store in localStorage that user dismissed the banner
    localStorage.setItem('notificationBannerDismissed', 'true');
    setNotificationPermission('dismissed');
  };

  const showNotificationBanner =
    notificationService.isNotificationSupported() &&
    notificationPermission === 'default' &&
    localStorage.getItem('notificationBannerDismissed') !== 'true';

  return (
    <>
      <StudentNewChatModal
        isOpen={isNewChatModalOpen}
        onClose={() => setIsNewChatModalOpen(false)}
        onConversationCreated={handleConversationCreated}
      />
    <div className="flex h-screen bg-gray-800" style={{ height: '100dvh' }}>
      {/* Notification Permission Banner */}
      {showNotificationBanner && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-3 shadow-lg">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-3 flex-1">
              <svg className="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-sm font-medium">
                Enable notifications to get alerts when you receive new messages
              </p>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={handleRequestNotificationPermission}
                className="bg-white text-blue-600 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors whitespace-nowrap"
              >
                Enable
              </button>
              <button
                onClick={handleDismissNotificationBanner}
                className="text-white hover:text-blue-100 transition-colors p-1"
                aria-label="Dismiss"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`flex-1 flex overflow-hidden ${showNotificationBanner ? 'pt-14' : ''}`}>
        {/* Conversation List */}
        <div className={`${
          showConversationList ? 'flex' : 'hidden'
        } md:flex w-full md:w-1/3 bg-gray-900 md:border-r border-gray-700 overflow-y-auto`}>
          <StudentConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            currentUserId={studentMongoId}
            loading={loading}
            onNewChat={() => setIsNewChatModalOpen(true)}
          />
        </div>

        {/* Message Area */}
        <div className={`${
          !showConversationList && selectedConversation ? 'flex' : 'hidden'
        } md:flex flex-1 flex-col w-full`}>
          {selectedConversation ? (
            <StudentMessageArea
              conversation={selectedConversation}
              messages={messages}
              currentUserId={studentMongoId}
              onSendMessage={handleSendMessage}
              onTyping={handleTyping}
              typingUser={typingUsers[selectedConversation.id]}
              isConnected={isConnected}
              onlineUsers={onlineUsers}
              lastSeen={lastSeen}
              onBack={() => setShowConversationList(true)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 bg-gray-800">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-300">No conversation selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default StudentChatInterface;
